import { Controller } from "@hotwired/stimulus";

/**
 * Password Reset Form Controller
 * Validates email format on the password reset form.
 * Email must have a valid domain with TLD (e.g., name@example.com)
 */

export default class extends Controller<HTMLFormElement> {
  static targets = ["email", "submit", "emailError"];

  declare readonly emailTarget: HTMLInputElement;
  declare readonly submitTarget: HTMLButtonElement;
  declare readonly emailErrorTarget: HTMLElement;
  declare readonly hasEmailErrorTarget: boolean;

  // Email regex: requires user@domain.tld format
  private readonly emailRegex =
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  connect(): void {
    this.validateForm();
    this.element.addEventListener("input", this.handleFormInput);
  }

  disconnect(): void {
    this.element.removeEventListener("input", this.handleFormInput);
  }

  private handleFormInput = (): void => {
    this.validateForm();
  };

  validateEmail(): void {
    const input = this.emailTarget;
    const value = input.value.trim();
    const isValid = this.emailRegex.test(value);

    this.toggleFieldValidation(input, isValid);
    this.updateErrorMessage(value, isValid);
    this.validateForm();
  }

  private validateForm(): void {
    requestAnimationFrame(() => {
      const email = this.emailTarget.value.trim();
      const isEmailValid = this.emailRegex.test(email);

      this.submitTarget.disabled = !isEmailValid;
    });
  }

  private updateErrorMessage(value: string, isValid: boolean): void {
    if (!this.hasEmailErrorTarget) return;

    if (!isValid && value.length > 0) {
      this.emailErrorTarget.textContent =
        "Please enter a valid email address (e.g., name@example.com)";
      this.emailErrorTarget.classList.remove("hidden");
    } else {
      this.emailErrorTarget.classList.add("hidden");
    }
  }

  private toggleFieldValidation(
    input: HTMLInputElement,
    isValid: boolean
  ): void {
    if (input.value.length === 0) {
      input.classList.remove("input-success", "input-error");
      return;
    }

    if (isValid) {
      input.classList.add("input-success");
      input.classList.remove("input-error");
    } else {
      input.classList.add("input-error");
      input.classList.remove("input-success");
    }
  }
}
